import moment from 'moment-timezone'
import { currentPricing } from '../docs/memberBenefits'

/**
 * The account page's "Membership term" card: the current term and, once the
 * member has renewed, the next one. Pure, so each case is tested on its own.
 */

const TZ = 'America/New_York'
const PENDING = ['emailed', 'in_progress', 'signed']

// liveRenewedUntil for an open-ended live renewal (no end date).
export const ONGOING = 'ongoing'
const PRICE_KEYS = { 'Golf': 'Golf', 'Social': 'Social', 'Golf Add-on': 'Golf_Addon', 'Social Add-on': 'Social_Addon' }

const fmt = (date) => (date ? moment.tz(date, TZ).format('MMM D, YYYY') : null)
const fromUnix = (seconds) => (seconds ? new Date(seconds * 1000) : null)

/** "$125/month + tax" for a membership type, from the same price list as renewal. */
export const rateFor = (type) => {
    const price = currentPricing[PRICE_KEYS[type]]
    return price && price.amount ? `${price.amount} + tax` : null
}

/**
 * {
 *   current: { start, end, daysLeft, expired, through } | null,
 *   next: { state: 'renewed' | 'in_progress' | 'not_renewed' | 'with_primary', start, end, rate } | null
 * }
 * Dates are display strings ("Nov 26, 2026").
 */
export const membershipTerms = ({
    subscriptions = [], scheduled = [], liveRenewedUntil = null, expired = false,
    covered = null, ownRenewal = null, type = null, now = new Date(),
    selfPayOf = null
}) => {
    const rate = rateFor(type)

    // An add-on on the primary's subscription: their term is the primary's.
    if (covered) {
        const first = (covered.expiresOn || [])[0] || null
        // The primary's membership is open-ended: so is theirs (no next term).
        if (first === 'Ongoing') {
            return { current: { start: null, end: null, ongoing: true, daysLeft: null, expired: false, through: covered.primaryName || 'your primary member' }, next: null }
        }
        const end = first
        const current = { start: null, end, daysLeft: null, expired: false, through: covered.primaryName || 'your primary member' }
        let next = { state: 'with_primary' }
        if (ownRenewal && ownRenewal.status === 'enrolled') {
            next = { state: 'renewed', start: fmt(ownRenewal.termStart), end: fmt(ownRenewal.termEnd), rate: null }
        } else if (ownRenewal && PENDING.indexOf(ownRenewal.status) !== -1) {
            next = { state: 'in_progress', start: fmt(ownRenewal.termStart), end: fmt(ownRenewal.termEnd) }
        }
        return { current, next }
    }

    const subs = subscriptions || []
    const sub = subs.find((s) => s && s.cancel_at) || subs[0] || null

    // Renewed after expiring: a new live subscription already runs the new term.
    if (liveRenewedUntil === ONGOING) {
        return {
            current: { start: sub && sub.start_date ? fmt(fromUnix(sub.start_date)) : null, end: null, ongoing: true, daysLeft: null, expired: false },
            next: null
        }
    }
    if (liveRenewedUntil) {
        const end = moment.tz(liveRenewedUntil, 'MM/DD/YYYY', TZ)
        return {
            current: { start: sub && sub.start_date ? fmt(fromUnix(sub.start_date)) : null, end: end.isValid() ? end.format('MMM D, YYYY') : liveRenewedUntil, daysLeft: null, expired: false },
            next: { state: 'renewed', start: null, end: null, rate, current: true }
        }
    }

    if (!sub) {
        return { current: expired ? { start: null, end: null, daysLeft: null, expired: true } : null, next: null }
    }

    // Open-ended (specs/open-ended-memberships): an active subscription with no
    // end date continues, billed monthly. No days left and no next term.
    if (!sub.cancel_at && !sub.ended_at && !sub.canceled_at && !expired) {
        return { current: { start: fmt(fromUnix(sub.start_date)), end: null, ongoing: true, daysLeft: null, expired: false }, next: null }
    }

    const endDate = fromUnix(sub.cancel_at || sub.ended_at || sub.canceled_at)
    const daysLeft = endDate && !expired ? Math.max(0, moment.tz(endDate, TZ).diff(moment.tz(now, TZ), 'days')) : null
    const current = { start: fmt(fromUnix(sub.start_date)), end: fmt(endDate), daysLeft, expired: !!expired }

    const phase = scheduled && scheduled[0] && scheduled[0].phases && scheduled[0].phases[0]
    // A 'release' schedule is open-ended: its phase end is only the first
    // billing month, not the end of the next term.
    const phaseEnd = phase && !(scheduled[0].end_behavior === 'release') ? fmt(fromUnix(phase.end_date)) : null

    // A self-pay add-on (own subscription, in a primary's household): their
    // next term is the add-on renewal, which opens only once the primary has
    // renewed - they cannot renew on their own.
    if (selfPayOf) {
        if (ownRenewal && ownRenewal.status === 'enrolled') {
            return { current, next: { state: 'renewed', start: fmt(ownRenewal.termStart), end: fmt(ownRenewal.termEnd), rate: null } }
        }
        if (ownRenewal && PENDING.indexOf(ownRenewal.status) !== -1) {
            return { current, next: { state: 'in_progress', start: fmt(ownRenewal.termStart), end: fmt(ownRenewal.termEnd) } }
        }
        if (phase) {
            return { current, next: { state: 'renewed', start: fmt(fromUnix(phase.start_date)), end: phaseEnd, rate } }
        }
        return { current, next: endDate ? { state: 'with_primary', primaryName: selfPayOf } : null }
    }

    if (phase) {
        return { current, next: { state: 'renewed', start: fmt(fromUnix(phase.start_date)), end: phaseEnd, rate } }
    }
    return { current, next: endDate ? { state: 'not_renewed', renewBy: current.end, rate } : null }
}
